import * as React from "react";
import axios from "axios";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import throttle from "lodash/throttle";
// components
import { FormattedMessage } from "react-intl";
import { FollowButton } from "common/components/followButton";
import ConfirmModal, { CONFIRM_RESPONSE } from "common/components/confirmModal";
// actions
import {
  changeCollectionNotifyStatus as changeCollectionNotifyStatusAction,
  followCollection as followCollectionAction,
  unFollowCollection as unFollowCollectionAction,
} from "@vingle/wtf-sdk/actions/collection";
import { ActionCreators as VingleDialogActionCreators } from "app/vingleDialog/actions";
import { pushToast as pushToastAction } from "@vingle/wtf-sdk/actions/toast";
// interfaces
import { AppDispatch } from "app/store";
import { IAppState } from "app/rootReducer";

function mapStateToProps(state: IAppState) {
  return {
    currentUser: state.app.currentUser,
  };
}

function mapDispatchToProps(dispatch: AppDispatch) {
  return bindActionCreators(
    {
      changeCollectionNotifyStatus: changeCollectionNotifyStatusAction,
      followCollection: followCollectionAction,
      unFollowCollection: unFollowCollectionAction,
      pushToast: pushToastAction,
      openSignInDialog: VingleDialogActionCreators.openSignInDialog,
    },
    dispatch,
  );
}

interface IProps
  extends ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps> {
  collectionId: Vingle.Id;
  isFollowing: boolean;
  onClickAfterButton?: () => void;
  onClickAfterNotificationActive?: () => void;
  onClickAfterNotificationMute?: () => void;
}

interface IState {
  isOpenNotificationConfirmModal: boolean;
}

class CollectionFollowButton extends React.PureComponent<IProps, IState> {
  public state: IState = {
    isOpenNotificationConfirmModal: false,
  };

  private cancelTokenSource = axios.CancelToken.source();

  public render() {
    const { isFollowing } = this.props;
    return (
      <>
        <FollowButton
          text={<FormattedMessage tagName="span" id="follow_plain" />}
          isFollowing={isFollowing}
          onFollowClick={this.handleCollectionFollowing}
        />
        {this.renderCollectionFollowNotificationDialog()}
      </>
    );
  }

  public componentWillUnmount() {
    this.cancelTokenRestore();
  }

  private readonly cancelTokenRestore = () => {
    this.cancelTokenSource.cancel();
    this.cancelTokenSource = axios.CancelToken.source();
  };

  // tslint:disable-next-line:member-ordering
  private readonly handleCollectionFollowing = throttle(async () => {
    const {
      collectionId,
      isFollowing,
      currentUser,
      openSignInDialog,
      followCollection,
      unFollowCollection,
      pushToast,
      onClickAfterButton,
    } = this.props;
    if (!currentUser) {
      openSignInDialog();
    } else {
      this.cancelTokenRestore();
      if (isFollowing) {
        unFollowCollection(collectionId, this.cancelTokenSource.token);
        pushToast({
          type: "SUCCESS",
          messageId: "profile.collection.un_follow",
        });
      } else {
        await followCollection({
          collectionId,
          cancelToken: this.cancelTokenSource.token,
        });
        this.openNotiConfirmModal();
      }
    }

    if (onClickAfterButton) onClickAfterButton();
  }, 300);

  private readonly openNotiConfirmModal = () => {
    this.setState({
      isOpenNotificationConfirmModal: true,
    });
  };

  private readonly renderCollectionFollowNotificationDialog = () => {
    const { isOpenNotificationConfirmModal } = this.state;
    return (
      <ConfirmModal
        open={isOpenNotificationConfirmModal}
        message={
          <span>
            <FormattedMessage id="profile.confirmModal.collection_notification.message" />
          </span>
        }
        cancelButtonText={
          <span>
            <FormattedMessage id="profile.confirmModal.collection_notification.no" />
          </span>
        }
        submitButtonText={
          <span>
            <FormattedMessage id="profile.confirmModal.collection_notification.yes" />
          </span>
        }
        onRequestClose={this.handleCollectionFollowDialogCloseRequest}
        onResponseCallback={this.handleCollectionFollowDialogResponse}
      />
    );
  };

  private readonly handleCollectionFollowDialogCloseRequest = () => {
    this.setState({
      isOpenNotificationConfirmModal: false,
    });
  };

  private readonly handleCollectionFollowDialogResponse = (
    response: CONFIRM_RESPONSE,
  ) => {
    const {
      collectionId,
      changeCollectionNotifyStatus,
      onClickAfterNotificationActive,
      onClickAfterNotificationMute,
    } = this.props;
    const enable = response === CONFIRM_RESPONSE.OK;
    changeCollectionNotifyStatus({
      collectionId,
      enable,
      cancelToken: this.cancelTokenSource.token,
    });
    if (enable) {
      if (onClickAfterNotificationActive) onClickAfterNotificationActive();
    } else {
      if (onClickAfterNotificationMute) onClickAfterNotificationMute();
    }
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CollectionFollowButton);
